import type { FetchApi } from '@backstage/core-plugin-api';
import {
  techRadarApiRef,
  type TechRadarApi,
} from '@backstage-community/plugin-tech-radar';
import type {
  RadarEntry,
  RadarEntrySnapshot,
  RadarQuadrant,
  RadarRing,
  TechRadarLoaderResponse,
} from '@backstage-community/plugin-tech-radar-common';
import {
  ApiBlueprint,
  configApiRef,
  createFrontendModule,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';

/**
 * Forma de `public/tech-radar.json` tal como está en disco: las fechas son
 * cadenas ISO y se convierten a `Date` al cargar.
 */
export interface RadarJsonRaw {
  quadrants: RadarQuadrant[];
  rings: RadarRing[];
  entries: Array<
    Omit<RadarEntry, 'timeline'> & {
      timeline: Array<Omit<RadarEntrySnapshot, 'date'> & { date: string }>;
    }
  >;
}

/** Convierte las fechas ISO del JSON en `Date`, que es lo que espera el plugin. */
export function convertirRadar(raw: RadarJsonRaw): TechRadarLoaderResponse {
  return {
    quadrants: raw.quadrants,
    rings: raw.rings,
    entries: raw.entries.map(entrada => ({
      ...entrada,
      timeline: entrada.timeline.map(momento => ({
        ...momento,
        date: new Date(momento.date),
      })),
    })),
  };
}

/**
 * Implementación de `TechRadarApi` que lee el radar estático servido desde
 * `packages/app/public/tech-radar.json`. Sustituye al backend de tech-radar,
 * que no existe en este portal.
 */
export class StaticTechRadarClient implements TechRadarApi {
  constructor(
    private readonly fetchApi: FetchApi,
    private readonly baseUrl: string,
  ) {}

  async load(_id: string | undefined): Promise<TechRadarLoaderResponse> {
    const url = `${this.baseUrl.replace(/\/+$/, '')}/tech-radar.json`;
    const respuesta = await this.fetchApi.fetch(url);
    if (!respuesta.ok) {
      throw new Error(
        `No se pudo cargar tech-radar.json (HTTP ${respuesta.status})`,
      );
    }
    return convertirRadar((await respuesta.json()) as RadarJsonRaw);
  }
}

/**
 * Override de `api:tech-radar`. Sin `name`: el id resultante debe ser
 * exactamente `api:tech-radar` para reemplazar la implementación por defecto.
 */
export const techRadarApiOverride = ApiBlueprint.make({
  params: define =>
    define({
      api: techRadarApiRef,
      deps: { fetchApi: fetchApiRef, configApi: configApiRef },
      factory: ({ fetchApi, configApi }) =>
        new StaticTechRadarClient(fetchApi, configApi.getString('app.baseUrl')),
    }),
});

/**
 * Módulo del plugin `tech-radar`. Los módulos reemplazan por id las
 * extensiones del plugin original; por eso se declara con `pluginId: 'tech-radar'`.
 */
export const techRadarModule = createFrontendModule({
  pluginId: 'tech-radar',
  extensions: [techRadarApiOverride],
});
